import { DataPeriod } from '@prisma/client';
import { Skeleton } from '@zond/ui';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { ReportAttendanceChartSkeleton } from './attendance/ReportAttendanceChartSkeleton';
import { ProjectUserSelector } from './project-user-selector';
import { ReportRangePicker } from './report-range-picker';

interface UserReportSkeletonProps {
  selectedRange: DataPeriod;
  selectedDate: Date;
  onRangeChange: (range: DataPeriod) => void;
  onDateChange: (date: Date) => void;
}

export const UserReportSkeleton: React.FC<UserReportSkeletonProps> = ({
  selectedRange,
  selectedDate,
  onRangeChange,
  onDateChange,
}) => {
  const { t } = useTranslation('reports');
  const { projectId, userId } = useParams();

  // Check if this is a project report
  const isProjectReport = userId === 'All';

  // Only show attendance chart for Weekly and Monthly views
  const showAttendanceChart = isProjectReport && selectedRange !== DataPeriod.Daily;

  return (
    <div className="h-[calc(100vh-64px)] flex flex-col">
      <div className="flex-none p-6 bg-card border-b">
        <div className="flex justify-between items-center">
          <h1 className="text-2xl font-bold">{t('title.userReport')}</h1>
          <div className="flex items-center gap-4">
            <ProjectUserSelector currentProjectId={projectId} currentUserId={userId} />
            <ReportRangePicker
              selectedRange={selectedRange}
              onRangeChange={onRangeChange}
              selectedDate={selectedDate}
              onDateChange={onDateChange}
            />
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-auto custom-scrollbar">
        <div className="p-6 pb-12 space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Stats cards placeholders */}
            {Array.from({ length: 4 }).map((_, index) => (
              <div key={index} className="p-4 bg-card rounded-lg shadow border">
                <Skeleton className="h-4 w-32 mb-2" />
                <Skeleton className="h-6 w-24" />
              </div>
            ))}
          </div>

          <div className="grid grid-cols-5 gap-6">
            {/* Report section skeleton */}
            <div className="col-span-3 bg-card rounded-lg shadow border h-[600px] flex flex-col">
              <div className="p-6">
                <Skeleton className="h-8 w-32" />
              </div>
              <div className="flex-1 p-6">
                <div className="space-y-6">
                  {Array.from({ length: 3 }).map((_, index) => (
                    <div key={index}>
                      <Skeleton className="h-6 w-1/3 mb-2" />
                      <Skeleton className="h-28 w-full" />
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Applications section skeleton */}
            <div className="col-span-2 bg-card rounded-lg shadow border h-[600px] flex flex-col">
              <div className="p-6">
                <Skeleton className="h-8 w-48" />
              </div>
              <div className="flex-1 flex items-center justify-center">
                <Skeleton className="h-64 w-64 rounded-full" />
              </div>
            </div>
          </div>

          {/* Add attendance chart skeleton for project reports - only for non-Daily periods */}
          {showAttendanceChart && (
            <div className="mb-6">
              <ReportAttendanceChartSkeleton />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
